import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/cn";
import { spring } from "@/lib/motion";

export function progressTone(ratio: number): string {
  if (ratio > 1) return "var(--negative)";
  if (ratio >= 0.85) return "var(--warning)";
  return "var(--accent)";
}

export function ProgressBar({
  ratio,
  className,
  color,
}: {
  ratio: number;
  className?: string;
  color?: string;
}) {
  const reduce = useReducedMotion();
  const pct = Math.max(0, Math.min(ratio, 1)) * 100;
  return (
    <div
      className={cn("h-2 overflow-hidden rounded-full bg-surface-2", className)}
      role="progressbar"
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <motion.div
        className="h-full rounded-full"
        style={{ background: color ?? progressTone(ratio) }}
        initial={{ width: reduce ? `${pct}%` : 0 }}
        animate={{ width: `${pct}%` }}
        transition={spring.gentle}
      />
    </div>
  );
}

export function ProgressRing({
  ratio,
  size = 56,
  stroke = 6,
  label,
}: {
  ratio: number;
  size?: number;
  stroke?: number;
  label?: string;
}) {
  const reduce = useReducedMotion();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(ratio, 1));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--surface-2)"
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={progressTone(ratio)}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: reduce ? c * (1 - clamped) : c }}
          animate={{ strokeDashoffset: c * (1 - clamped) }}
          transition={spring.gentle}
        />
      </svg>
      <span className="absolute inset-0 grid place-items-center tabular text-caption font-semibold">
        {label ?? `${Math.round(ratio * 100)}%`}
      </span>
    </div>
  );
}
